import { computed, ref, type Ref } from "vue";
import {
  arrayUnion,
  arrayRemove,
  type FirestoreError,
} from "firebase/firestore";
import { handleFirestoreException } from "@/helpers/exceptions/handler";
import { setErrorToast } from "@/helpers/notifications/toast";
import { updateEventById } from "@/helpers/firestore/EventService";
import { useUserStore } from "@/stores/modules/user/user";
import { useAuthStore } from "@/stores/modules/auth/auth";
import type { EventDocument } from "@kekkle/shared/types/documents/event-document/event-document";

/**
 * Composable for managing event presence status (available/maybe/not available)
 */
export function useEventPresence(event: Ref<EventDocument>) {
  const updating = ref(false);
  const userStore = useUserStore();
  const authStore = useAuthStore();

  const userId = computed(() => authStore.authUuid);

  const isAvailable = computed(() => {
    const uid = userId.value;
    return !!(
      uid &&
      event.value.available &&
      event.value.available.includes(uid)
    );
  });

  const isMaybeAvailable = computed(() => {
    const uid = userId.value;
    return !!(
      uid &&
      event.value.maybe_available &&
      event.value.maybe_available.includes(uid)
    );
  });

  const isNotAvailable = computed(() => {
    const uid = userId.value;
    return !!(
      uid &&
      event.value.non_available &&
      event.value.non_available.includes(uid)
    );
  });

  async function updatePresence(payload: Record<string, unknown>) {
    if (updating.value || !event.value || !userStore.activeGroup) {
      return;
    }

    updating.value = true;

    try {
      await updateEventById(event.value._id, userStore.activeGroup, payload);
      // onSnapshot listener in eventsStore updates state automatically
    } catch (e) {
      const message = handleFirestoreException({ e: e as FirestoreError });
      setErrorToast({ message });
    } finally {
      updating.value = false;
    }
  }

  async function setAvailable() {
    if (!userId.value) return;

    await updatePresence({
      available: arrayUnion(userId.value),
      maybe_available: arrayRemove(userId.value),
      non_available: arrayRemove(userId.value),
    });
  }

  async function setMaybe() {
    if (!userId.value) return;

    await updatePresence({
      available: arrayRemove(userId.value),
      maybe_available: arrayUnion(userId.value),
      non_available: arrayRemove(userId.value),
    });
  }

  async function setNotAvailable() {
    if (!userId.value) return;

    await updatePresence({
      available: arrayRemove(userId.value),
      maybe_available: arrayRemove(userId.value),
      non_available: arrayUnion(userId.value),
    });
  }

  return {
    updating,
    isAvailable,
    isMaybeAvailable,
    isNotAvailable,
    setAvailable,
    setMaybe,
    setNotAvailable,
  };
}
